// PAGE / ROUTE MOTION (HOME_AMBIENT_SKIN_SPEC §6 · DESIGN_SPEC §4.6–4.7, §6.4).
//
//   el.call('routeTransitionEnter', shellRoot)  — the NEW page root rises in:
//       `fadeInUpShort` (6.5 % travel), `C` on `springBezier`
//   el.call('routeTransitionExit', pageEl)      — the LEAVING page fades: `B` on
//       `defaultBezier`, opacity only, one letter faster than the entrance;
//       resolves true when it ran, false when it was skipped
//
// Opacity + transform only. No motion, and the final state at once, when:
//   • the OS asks for reduced motion (`prefers-reduced-motion: reduce`);
//   • the person asked for it in the app (`root.a11yMotion`, Settings ›
//     Accessibility — "transitions and animations stand still");
//   • the tab is not visible — WAAPI events are frame-gated, and animating for
//     nobody is pure risk (the PageSlot rule, canvas/components/PageSlot.js);
//   • a token is missing — the duration, the curve and the keyframes are read
//     off the RUNNING design system (`context.designSystem`: the timing
//     sequence's `C` / `B` values, `springBezier` / `defaultBezier`, the
//     `fadeInUpShort` keyframes). Nothing here is a literal duration or curve;
//     a design system without them gets no motion rather than a guess.
//
// WHY WAAPI, like PageSlot: a routed page is a fresh element on every
// navigation, and a one-shot `node.animate()` owns its run without re-rendering
// the page it animates (a css-in-props animation would need a prop the page
// never declared). The page root is the router's content element
// (`shellRoot.content`, @symbo.ls/router `contentElementKey: 'content'`).
//
// WHERE IT HOOKS. The router swaps pages synchronously
// (`element.set(nextContent, …)` in @symbo.ls/router `router()`) and then fires
// `routeChanged` on the shell root — so the ENTRANCE hooks the shell root's
// `onRouteChanged` (docs/patches/route-transition.patch; app.js is ws-w2's).
// The EXIT has no hook to run from: the leaving element is disposed inside the
// same synchronous call, before any shell code sees the navigation. It is
// ready for a router pre-swap hook (framework request on the ticket).

export function routeTransitionEnter(shell) {
  const el = shell || this
  const page = el && el.content
  const node = page && page.node
  if (!node || typeof node.animate !== 'function') return null
  // Only a NEW page root enters. The router fires `routeChanged` on every
  // call — a query-only navigation keeps the same content element — and the
  // first route of a session is the boot, which the boot loader hands off,
  // not a navigation. The last page root seen rides on the shell element
  // (non-reactive bookkeeping, the house `__x` idiom).
  const last = el.__routeEnterNode
  el.__routeEnterNode = node
  if (!last || last === node) return null
  const doc = node.ownerDocument
  if (!doc || doc.visibilityState !== 'visible') return null
  const win = doc.defaultView
  if (
    win &&
    typeof win.matchMedia === 'function' &&
    win.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return null
  }
  const st = el.state
  const root = (st && st.root) || st
  if (root && root.a11yMotion) return null
  const ds = el.context && el.context.designSystem
  const timing = ds && ds.timing
  const step = timing && timing.sequence && timing.sequence.C
  const duration = step && Number(step.val)
  const easing = timing && timing.springBezier
  const frames = ds && ds.animation && ds.animation.fadeInUpShort
  if (!duration || !easing || !frames || !frames.from || !frames.to) return null
  const anim = node.animate([frames.from, frames.to], { duration, easing })
  anim.id = 'routeEnter'
  return anim
}

export function routeTransitionExit(page) {
  const el = this
  const node = page && page.node
  if (!node || typeof node.animate !== 'function') return Promise.resolve(false)
  const doc = node.ownerDocument
  if (!doc || doc.visibilityState !== 'visible') return Promise.resolve(false)
  const win = doc.defaultView
  if (
    win &&
    typeof win.matchMedia === 'function' &&
    win.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return Promise.resolve(false)
  }
  const st = el && el.state
  const root = (st && st.root) || st
  if (root && root.a11yMotion) return Promise.resolve(false)
  const ds = el && el.context && el.context.designSystem
  const timing = ds && ds.timing
  const step = timing && timing.sequence && timing.sequence.B
  const duration = step && Number(step.val)
  const easing = timing && timing.defaultBezier
  if (!duration || !easing) return Promise.resolve(false)
  const anim = node.animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing })
  anim.id = 'routeExit'
  return Promise.resolve(anim.finished).then(
    () => true,
    () => true
  )
}
